import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Users, QrCode, Radio, BarChart3, Settings, LayoutDashboard } from "lucide-react";
import { Panel, Eyebrow, LiveDot, MonoLabel } from "@/components/Panel";
import { SPONSORS } from "@/data/seed";

const CARDS = [
  { to: "/register", name: "Public Registration",  badge: "PUBLIC",  icon: Users,           blurb: "Member & stall forms, tiered fees, mock Razorpay checkout and QR pass reveal." },
  { to: "/member",   name: "Member Portal",        badge: "MEMBER",  icon: QrCode,          blurb: "Mock OTP login, digital pass, and personalised conclave schedule." },
  { to: "/leader",   name: "Table Leader App",     badge: "HERO",    icon: Radio,           blurb: "Round-table seating graphic. Drag or tap photos to log referrals live." },
  { to: "/scanner",  name: "Scanner App",          badge: "STAFF",   icon: QrCode,          blurb: "Meal check-in scanner with success/reject flash and reason." },
  { to: "/admin",    name: "Admin Dashboard",      badge: "ORG",     icon: BarChart3,       blurb: "KPIs, funnel, WhatsApp analytics and a live referral leaderboard." },
  { to: "/big-screen", name: "Live Big Screen",    badge: "STAGE",   icon: LayoutDashboard, blurb: "Venue-scale conclave leaderboard with rolling live feed for the projector." },
  { to: "/devadmin", name: "DevAdmin Console",     badge: "SUPER",   icon: Settings,        blurb: "Integration keys, meal windows and tier fee configuration." },
];

const MARQUEE = [
  ["Region", "Davangere"], ["Chapter", "Jal"], ["Visiting", "Bengaluru"], ["Visiting", "Hubballi"],
  ["Visiting", "Mysuru"], ["Delegate", "Dubai"], ["Delegate", "Singapore"], ["Visiting", "Chennai"],
  ["Visiting", "Hyderabad"], ["Delegate", "UK"],
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-grid">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <Eyebrow>BNI Davangere Region · Members Day 2026</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="h-hero mt-4 text-4xl sm:text-6xl lg:text-7xl max-w-[900px]"
          >
            One event. Six screens.<br/>
            <span style={{ color: "var(--red-2)" }}>The whole day, wired up.</span>
          </motion.h1>
          <p className="mt-6 max-w-[640px] text-base sm:text-lg" style={{ color: "var(--body)" }}>
            A clickable walkthrough of the Members Day platform — registration, payment, QR check-in,
            the one-to-one conclave, and the organizer command centre. Every flow is live and every screen is real.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-register" className="btn-red">
              Start the walkthrough <ArrowRight size={16} />
            </Link>
            <Link to="/admin" data-testid="hero-cta-admin" className="btn-ghost">
              <LayoutDashboard size={14} /> Jump to Admin Dashboard
            </Link>
            <span className="inline-flex items-center gap-2 ml-1"><LiveDot label="MID-EVENT · SIMULATED" /></span>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-0 hairline">
            {[
              ["49", "Chapters"], ["1,841", "Members"], ["15", "Cities"], ["6", "Countries"],
            ].map(([n, l], i) => (
              <div key={l} className={`p-5 ${i < 3 ? "sm:border-r border-[var(--hairline)]" : ""} ${i < 2 ? "border-r border-[var(--hairline)]" : ""}`}>
                <div className="font-serif text-3xl sm:text-4xl">{n}</div>
                <div className="mono-label mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="marquee">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map(([label, val], i) => (
            <span key={i} className="chip">
              {label} <strong style={{color:"var(--text)", marginLeft:6}}>{val}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* Sponsor strip */}
      <SponsorStrip />

      {/* Screens grid */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <Eyebrow>The Six Screens</Eyebrow>
            <h2 className="h-section text-3xl sm:text-4xl mt-3">Every role in the room — on one platform.</h2>
          </div>
          <MonoLabel>Tap any card to enter</MonoLabel>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((c, i) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`landing-card-${c.badge.toLowerCase()}`}
              className={`panel p-6 group hover:border-[var(--red)] transition-colors ${i % 3 !== 2 ? "md:border-r-0" : ""} ${i < CARDS.length - 3 ? "md:border-b-0" : ""}`}
              style={{ marginLeft: i === 0 ? 0 : -1, marginTop: i < 3 ? 0 : -1 }}
            >
              <div className="flex items-start justify-between">
                <c.icon size={22} style={{ color: "var(--red-2)" }} />
                <span className="mono-label px-2 py-1 border" style={{ color: "var(--red)", borderColor: "rgba(204,37,47,0.4)" }}>{c.badge}</span>
              </div>
              <div className="mt-6 font-serif text-2xl group-hover:text-[var(--text)]">{c.name}</div>
              <p className="mt-3 text-sm" style={{ color: "var(--body)" }}>{c.blurb}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm" style={{ color: "var(--red-2)" }}>
                Enter <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="hairline-t">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8 flex items-center justify-between flex-wrap gap-3">
          <div className="mono-label">Trishul Kalaabhavana · Davangere · 28 Nov 2026</div>
          <div className="mono-label" style={{ color: "var(--taupe)" }}>
            Powered by{" "}
            <a
              href="https://zerolabsdigi.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-zerolabs-link"
              style={{ color: "var(--red-2)", textDecoration: "underline", textUnderlineOffset: "3px" }}
            >
              Zerolabs Digitals
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SponsorStrip() {
  // Rotate a highlighted sponsor every 3.2s while others tile beneath
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SPONSORS.length), 3200);
    return () => clearInterval(t);
  }, []);
  const spotlight = SPONSORS[idx];
  return (
    <div className="hairline-b" style={{ background: "var(--panel)" }} data-testid="sponsor-strip">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-5 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-3">
          <div className="eyebrow">Our Partners</div>
          <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>{SPONSORS.length} brands · Region-wide</div>
        </div>
        <div className="lg:col-span-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={spotlight.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              data-testid={`sponsor-spotlight-${spotlight.id}`}
              className="flex items-center gap-4"
            >
              <div
                className="w-14 h-14 flex items-center justify-center font-serif text-2xl border-2"
                style={{ borderColor: spotlight.color, color: spotlight.color, background: "rgba(255,255,255,0.02)" }}
              >
                {spotlight.name.charAt(0)}
              </div>
              <div>
                <div className="mono-label" style={{ color: spotlight.color }}>{spotlight.tier} · SPOTLIGHT</div>
                <div className="font-serif text-2xl mt-1">{spotlight.name}</div>
                <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{spotlight.tagline}</div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-0 hairline">
          {SPONSORS.slice(0, 6).map((s, i) => {
            const active = s.id === spotlight.id;
            return (
              <div
                key={s.id}
                data-testid={`sponsor-tile-${s.id}`}
                className={`p-3 flex flex-col items-center justify-center text-center ${i % 3 !== 2 ? "border-r border-[var(--hairline)]" : ""} ${i < 3 ? "" : "border-t"}`}
                style={{ background: active ? "rgba(204,37,47,0.06)" : "transparent" }}
              >
                <div className="w-8 h-8 flex items-center justify-center font-serif text-sm border" style={{ borderColor: active ? s.color : "var(--hairline)", color: active ? s.color : "var(--body)" }}>
                  {s.name.charAt(0)}
                </div>
                <div className="mono-label mt-2 truncate w-full" style={{ color: active ? "var(--text)" : "var(--taupe)", fontSize: 9 }}>{s.name}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
